import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Landing from "@/pages/Landing";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import CompanyPublic from "@/pages/CompanyPublic";
import PublicProfile from "@/pages/PublicProfile";
import Network from "@/pages/Network";
import Market from "@/pages/Market";
import MarketListing from "@/pages/MarketListing";
import MarketOrderSuccess from "@/pages/MarketOrderSuccess";
import MarketOrderCancel from "@/pages/MarketOrderCancel";
import Business from "@/pages/Business";
import InvoiceDetail from "@/pages/InvoiceDetail";
import PublicInvoice, { PublicInvoiceSuccess, PublicInvoiceCancel } from "@/pages/PublicInvoice";
import WalletPage from "@/pages/Wallet";
import SubscriptionSuccess, { SubscriptionCancel } from "@/pages/SubscriptionSuccess";
import Recruitment from "@/pages/Recruitment";
import Settings from "@/pages/Settings";
import Ads from "@/pages/Ads";
import Revenues from "@/pages/Revenues";
import Prospection from "@/pages/Prospection";
import Portfolio from "@/pages/Portfolio";
import CandidatePortfolio from "@/pages/CandidatePortfolio";
import AdminOps from "@/pages/AdminOps";
import AdminLists from "@/pages/AdminLists";
import ProspectionConfig from "@/pages/ProspectionConfig";
import Brochures from "@/pages/Brochures";
import AdPaymentSuccess, { AdPaymentCancel } from "@/pages/AdPayment";
import Goals from "@/pages/Goals";
import AgentActivity from "@/pages/AgentActivity";
import AgentWidget from "@/components/agent/AgentWidget";
import TalentPool from "@/pages/TalentPool";
import Pipeline from "@/pages/Pipeline";
import OfferMatches from "@/pages/OfferMatches";
import Messages from "@/pages/Messages";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import AuthCallback from "@/pages/AuthCallback";

const LoadingScreen = () => (
  <div data-testid="loading-screen" className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (location.state?.user) return children;
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  // Check URL fragment synchronously during render to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/jobs" element={<Jobs />} />
      <Route path="/jobs/:offerId" element={<JobDetail />} />
      <Route path="/entreprises/:companyId" element={<CompanyPublic />} />
      <Route path="/profil/:handle" element={<PublicProfile />} />
      <Route path="/marche" element={<Market />} />
      <Route path="/marche/commande/success" element={<MarketOrderSuccess />} />
      <Route path="/marche/commande/cancel" element={<MarketOrderCancel />} />
      <Route path="/marche/:listingId" element={<MarketListing />} />
      <Route path="/facture/:token" element={<PublicInvoice />} />
      <Route path="/facture/:token/success" element={<PublicInvoiceSuccess />} />
      <Route path="/facture/:token/cancel" element={<PublicInvoiceCancel />} />
      <Route
        path="/business"
        element={
          <ProtectedRoute>
            <Business />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business/factures/:invoiceId"
        element={
          <ProtectedRoute>
            <InvoiceDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portefeuille"
        element={
          <ProtectedRoute>
            <WalletPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portefeuille/abonnement/success"
        element={
          <ProtectedRoute>
            <SubscriptionSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portefeuille/abonnement/cancel"
        element={
          <ProtectedRoute>
            <SubscriptionCancel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recrutement"
        element={
          <ProtectedRoute>
            <Recruitment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parametres"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/publicite"
        element={
          <ProtectedRoute>
            <Ads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/revenus"
        element={
          <ProtectedRoute>
            <Revenues />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prospection"
        element={
          <ProtectedRoute>
            <Prospection />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portfolio"
        element={
          <ProtectedRoute>
            <Portfolio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/talent/candidat/:candidateId"
        element={
          <ProtectedRoute>
            <CandidatePortfolio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pilotage"
        element={
          <ProtectedRoute>
            <AdminOps />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/listes/:type"
        element={
          <ProtectedRoute>
            <AdminLists />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prospection/configuration"
        element={
          <ProtectedRoute>
            <ProspectionConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prospection/brochures"
        element={
          <ProtectedRoute>
            <Brochures />
          </ProtectedRoute>
        }
      />
      <Route
        path="/publicite/paiement/success"
        element={
          <ProtectedRoute>
            <AdPaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/publicite/paiement/cancel"
        element={
          <ProtectedRoute>
            <AdPaymentCancel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reseau"
        element={
          <ProtectedRoute>
            <Network />
          </ProtectedRoute>
        }
      />
      <Route
        path="/objectifs"
        element={
          <ProtectedRoute>
            <Goals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ia-activite"
        element={
          <ProtectedRoute>
            <AgentActivity />
          </ProtectedRoute>
        }
      />
      <Route
        path="/talent"
        element={
          <ProtectedRoute>
            <TalentPool />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline"
        element={
          <ProtectedRoute>
            <Pipeline />
          </ProtectedRoute>
        }
      />
      <Route
        path="/offers/:offerId/matches"
        element={
          <ProtectedRoute>
            <OfferMatches />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:applicationId"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        }
      />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <AgentWidget />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
